import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useGeolocation } from '../hooks/useGeolocation';
import { savePendingData } from '../utils/offlineStorage';
import { useSurvey } from './SurveyContext';
import { COLLECTIONS } from '../utils/database/collections';
import { RecordingStatusToast } from '../modules/roadscope/components/RecordingStatusToast';
import { StopRecordingModal } from '../modules/roadscope/components/StopRecordingModal';
import { logInfo } from '../utils/logger';

const RecordingContext = createContext(null);

export const RecordingProvider = ({ children }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [showStopModal, setShowStopModal] = useState(false);
  const { startTracking, stopTracking } = useGeolocation();
  const { activeSurvey, updateSurvey } = useSurvey();
  const trackingCleanupRef = useRef(null);
  const surveyUpdateRef = useRef(null);

  // Update survey status when recording changes
  useEffect(() => {
    if (activeSurvey) {
      const newStatus = isRecording ? 'in_progress' : 'paused';
      
      // Store current status to prevent unnecessary updates
      if (surveyUpdateRef.current !== newStatus && 
          activeSurvey.status !== newStatus && 
          activeSurvey.status !== 'pending_review') {
        surveyUpdateRef.current = newStatus;
        updateSurvey({
          ...activeSurvey,
          status: newStatus,
          updatedAt: new Date().toISOString()
        });
        logInfo(`Survey status updated to ${newStatus}`);
      }
    }
  }, [isRecording, activeSurvey?.id, activeSurvey?.status, updateSurvey]);

  // Stop recording if survey becomes inactive
  useEffect(() => {
    if (!activeSurvey && isRecording) {
      setIsRecording(false);
      if (trackingCleanupRef.current) {
        trackingCleanupRef.current();
        trackingCleanupRef.current = null;
      }
      logInfo('Recording stopped due to inactive survey');
    }
  }, [activeSurvey, isRecording]);

  const toggleRecording = useCallback(() => {
    if (!activeSurvey) return;
    
    if (isRecording) {
      setShowStopModal(true);
      return;
    }

    setIsRecording(prev => {
      if (!prev) {
        // Start tracking and store cleanup function
        trackingCleanupRef.current = startTracking((point) => {
          // Validate and clean point data before saving
          const cleanPoint = {
            latitude: Number(point.latitude),
            longitude: Number(point.longitude),
            timestamp: new Date().toISOString(),
            accuracy: point.accuracy ? Number(point.accuracy) : null,
            altitude: point.altitude ? Number(point.altitude) : null,
            speed: point.speed ? Number(point.speed) : null,
            surveyId: activeSurvey.id
          };

          savePendingData(COLLECTIONS.TRACK_POINTS, {
            ...cleanPoint
          });
          logInfo('Track point saved');
        });
        logInfo('Location tracking started');
        return true;
      } else {
        return prev;
      }
    });
  }, [activeSurvey, startTracking]);

  const handlePauseRecording = useCallback(() => {
    setIsRecording(false);
    if (trackingCleanupRef.current) {
      trackingCleanupRef.current();
      trackingCleanupRef.current = null;
    }
    setShowStopModal(false);
    logInfo('Recording paused');
  }, []);

  const handleSaveRecording = useCallback(async () => {
    setIsRecording(false);
    if (trackingCleanupRef.current) {
      trackingCleanupRef.current();
      trackingCleanupRef.current = null;
    }
    // Save route logic will be handled by the route manager
    setShowStopModal(false);
    logInfo('Recording saved as route');
  }, []);

  const handleDeleteRecording = useCallback(() => {
    setIsRecording(false);
    if (trackingCleanupRef.current) {
      trackingCleanupRef.current();
      trackingCleanupRef.current = null;
    }
    setShowStopModal(false);
    logInfo('Recording deleted');
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (trackingCleanupRef.current) {
        trackingCleanupRef.current();
        trackingCleanupRef.current = null;
      }
    };
  }, []);

  return (
    <RecordingContext.Provider value={{ 
      isRecording, 
      toggleRecording,
      showStopModal,
      setShowStopModal,
      handlePauseRecording,
      handleSaveRecording,
      handleDeleteRecording
    }}>
      {children}
      {isRecording && <RecordingStatusToast />}
      {showStopModal && (
        <StopRecordingModal
          onClose={() => setShowStopModal(false)}
          onPause={handlePauseRecording}
          onSave={handleSaveRecording}
          onDelete={handleDeleteRecording}
        />
      )}
    </RecordingContext.Provider>
  );
};

export const useRecording = () => {
  const context = useContext(RecordingContext);
  if (!context) {
    throw new Error('useRecording must be used within a RecordingProvider');
  }
  return context;
};